import { type FieldMetadata, getFormProps, type useForm } from '@conform-to/react';
import { EnvelopeIcon } from '@heroicons/react/16/solid';
import { type FetcherWithComponents, Link } from '@remix-run/react';
import { Button } from 'flowbite-react';
import { useLocale } from 'remix-i18next/react';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { FormInputFieldWithIcon } from '#common/components/catalyst-ui-extensions/FormInputFieldWithIcon.js';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useTranslatedPath } from '#common/utils/i18n.tsx';

export const forgotPasswordSchema = z.object({
    email: z.string().email()
});

export type TForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordProps {
    forgotPasswordFetcher: FetcherWithComponents<any>;
    form: ReturnType<typeof useForm<TForgotPasswordFormData>>[0];
    fields: {
        email: FieldMetadata<string>;
    };
}

export function ForgotPassword({ forgotPasswordFetcher, form, fields }: ForgotPasswordProps) {
    const { t, rawT } = useEditableTranslation('login');
    const locale = useLocale();
    const translatePath = useTranslatedPath(locale);

    return (
        <>
            <forgotPasswordFetcher.Form method='POST' {...getFormProps(form)}>
                <HoneypotInputs />
                <div>
                    <FormInputFieldWithIcon
                        fieldClassName='mb-6'
                        field={fields.email}
                        icon={<EnvelopeIcon className='w-5 h-5 text-gray-500' />}
                        placeholder={rawT('email')}
                        autoFocus
                    />
                    <Button type='submit' className='w-full h-10 cursor-pointer mb-6'>{t('resetPassword')}</Button>
                </div>
            </forgotPasswordFetcher.Form>
            <Link
                to={translatePath('/login')}
                className='text-sm mt-5 underline cursor-pointer text-center font-medium dark:text-crispyblue-500 text-crispyblue-700'
            >
                {t('backToLogin')}
            </Link>
        </>
    );
}
